import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { StatsPageInfo, StatCard } from '~/components/Shared/StatsPageInfo/StatsPageInfo';

interface RequestsStatistics {
  total: number | null;
  created: number | null;
  in_progress: number | null;
  done: number | null;
  closed: number | null;
  expired: number | null;
}

interface Props {
  statusFilter: string;
  setStatusFilter: (value: string) => void;
}

const AllIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const CreatedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);

const InProgressIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const DoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const ClosedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const ExpiredIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

export const RequestsPageInfo = ({ statusFilter, setStatusFilter }: Props) => {
  const { t } = useTranslation();

  const { data: stats } = useQuery<RequestsStatistics>({
    queryFn: async () => {
      const response = await instance.get('admin/ticket-statistics');
      return response.data;
    },
    queryKey: ['ticket-statistics'],
  });

  const cards: StatCard[] = [
    { value: '', label: t('total'), count: stats?.total, color: 'purple', icon: <AllIcon /> },
    { value: '1', label: t('created'), count: stats?.created, color: 'blue', icon: <CreatedIcon /> },
    { value: '2', label: t('in_progress'), count: stats?.in_progress, color: 'orange', icon: <InProgressIcon /> },
    { value: '3', label: t('done'), count: stats?.done, color: 'green', icon: <DoneIcon /> },
    { value: '4', label: t('closed'), count: stats?.closed, color: 'gray', icon: <ClosedIcon /> },
    { value: '5', label: t('expired'), count: stats?.expired, color: 'red', icon: <ExpiredIcon /> },
  ];

  return (
    <StatsPageInfo
      activeStatus={statusFilter}
      onStatusChange={setStatusFilter}
      cards={cards}
    />
  );
};
